/**
 * Template Editor Component
 *
 * HTML editor with real-time validation, tabs (Editor/Preview PDF), and auto-save
 * Part of Template Builder (Phase 2)
 */

import { html as htmlLanguage } from "@codemirror/lang-html";
import { EditorView } from "@codemirror/view";
import { Tabs, Stack, Alert, Text, Group, Badge, Button, Grid, Paper, Tooltip } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconAlertCircle, IconCheck, IconCode, IconFileTypePdf, IconPhoto } from "@tabler/icons-react";
import CodeMirror, { type ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { useState, useEffect, useCallback, useRef } from "react";
import { ImageManager } from "./ImageManager";
import { TemplatePreview } from "./TemplatePreview";
import { VariableHelper } from "./VariableHelper";
import type { TemplateImage } from "../../types/template";
import { sanitizeHTML, validateHTMLStructure } from "../../utils/html-sanitizer";

export interface TemplateEditorProperties {
  /** Initial HTML content */
  initialHtml?: string;
  /** Callback when HTML changes (debounced) */
  onSave?: (html: string) => void | Promise<void>;
  /** Callback when HTML changes (immediate) */
  onChange?: (html: string) => void;
  /** Whether to enable auto-save */
  autoSave?: boolean;
  /** Debounce delay in ms (default: 2000) */
  debounceMs?: number;
  /** Read-only mode */
  readonly?: boolean;
  /** Custom placeholder */
  placeholder?: string;
  /** Show variable helper panel (default: false) */
  showVariableHelper?: boolean;
  /** Questionário ID for loading grupos in variable helper */
  questionarioId?: string;
  /** Auto-insert variable at cursor position when selected (default: true) */
  autoInsertVariable?: boolean;
  /** Template ID for image management */
  templateId?: string;
  /** Current template images */
  templateImages?: TemplateImage[];
  /** Show images tab (default: true if templateId provided) */
  showImagesTab?: boolean;
  /** Hide internal save button (useful when rendering save button externally) */
  hideSaveButton?: boolean;
  /** Callback to expose save state to parent */
  onSaveStateChange?: (state: { isSaving: boolean; isValid: boolean; save: () => void }) => void;
}

interface ValidationState {
  isValid: boolean;
  issues: string[];
  hadDangerousContent: boolean;
  sanitizedHtml: string;
}

/**
 * TemplateEditor - HTML editor with validation and auto-save
 * @param root0
 * @param root0.initialHtml
 * @param root0.onSave
 * @param root0.onChange
 * @param root0.autoSave
 * @param root0.debounceMs
 * @param root0.placeholder
 * @param root0.showVariableHelper
 * @param root0.questionarioId
 * @param root0.autoInsertVariable
 * @param root0.templateId
 * @param root0.templateImages
 * @param root0.showImagesTab
 * @param root0.readonly
 */
// Magic numbers and constants
const DEFAULT_DEBOUNCE_MS = 2000;
const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = 3600;
const MS_IN_SECOND = 1000;
const MAX_CHAR_LIMIT = 51_200;
const DEFAULT_TEMPLATE_IMAGES: TemplateImage[] = [];
const CODEMIRROR_EXTENSIONS = [htmlLanguage(), EditorView.lineWrapping];
const CODEMIRROR_BASIC_SETUP = {
  lineNumbers: true,
  highlightActiveLineGutter: true,
  highlightSpecialChars: true,
  foldGutter: true,
  drawSelection: true,
  dropCursor: true,
  allowMultipleSelections: true,
  indentOnInput: true,
  bracketMatching: true,
  closeBrackets: true,
  autocompletion: true,
  rectangularSelection: true,
  crosshairCursor: false,
  highlightActiveLine: true,
  highlightSelectionMatches: true,
  closeBracketsKeymap: true,
  searchKeymap: true,
  foldKeymap: true,
  completionKeymap: true,
  lintKeymap: true
};

/**
 *
 * @param html
 */
function normalizeHtml(html: string): string {
  const lines = html.split("\n");
  // Only trim completed lines (not the last one being actively typed)
  // This prevents trimEnd() from removing spaces during character-by-character typing
  return lines.map((line, index) => (index < lines.length - 1 ? line.trimEnd() : line)).join("\n");
}

/**
 *
 * @param lastSaved
 */
function formatLastSaved(lastSaved: Date | null): string | null {
  if (!lastSaved) return null;
  const now = new Date();
  const diff = Math.floor((now.getTime() - lastSaved.getTime()) / MS_IN_SECOND);

  if (diff < SECONDS_IN_MINUTE) return "agora mesmo";
  if (diff < SECONDS_IN_HOUR) return `há ${Math.floor(diff / SECONDS_IN_MINUTE)} min`;
  return lastSaved.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit"
  });
}

/**
 *
 * @param root0
 * @param root0.validation
 * @param root0.isSaving
 * @param root0.lastSaved
 * @param root0.autoSave
 * @param root0.onSave
 * @param root0.handleManualSave
 * @param root0.readonly
 * @param root0.hideSaveButton
 */
function EditorHeader({
  validation,
  isSaving,
  lastSaved,
  autoSave,
  onSave,
  handleManualSave,
  readonly,
  hideSaveButton
}: {
  validation: ValidationState;
  isSaving: boolean;
  lastSaved: Date | null;
  autoSave: boolean;
  onSave?: (html: string) => void | Promise<void>;
  handleManualSave: () => void;
  readonly: boolean;
  hideSaveButton: boolean;
}) {
  return (
    <Group justify="space-between">
      <Group gap="xs">
        {/* Validation status */}
        {validation.isValid ? (
          <Badge color="green" variant="light" leftSection={<IconCheck size={14} />}>
            HTML válido
          </Badge>
        ) : (
          <Badge color="red" variant="light" leftSection={<IconAlertCircle size={14} />}>
            {validation.issues.length} {validation.issues.length === 1 ? "erro" : "erros"}
          </Badge>
        )}

        {/* Dangerous content warning */}
        {validation.hadDangerousContent && (
          <Tooltip
            label={
              validation.issues.length > 0 ? validation.issues.join(", ") : "Conteúdo foi modificado pelo sanitizador"
            }
            multiline
            w={300}
          >
            <Badge color="yellow" variant="light" style={{ cursor: "help" }}>
              Conteúdo removido ({validation.issues.length})
            </Badge>
          </Tooltip>
        )}

        {/* Saving status */}
        {isSaving && (
          <Badge color="blue" variant="light">
            Salvando...
          </Badge>
        )}

        {/* Last saved */}
        {!isSaving && lastSaved && (
          <Text size="xs" c="dimmed">
            Salvo {formatLastSaved(lastSaved)}
          </Text>
        )}
      </Group>

      {/* Manual save button (hidden when rendered externally) */}
      {!hideSaveButton && !autoSave && onSave && (
        <Button onClick={handleManualSave} loading={isSaving} disabled={!validation.isValid || readonly}>
          Salvar
        </Button>
      )}
    </Group>
  );
}

/**
 *
 * @param root0
 * @param root0.initialHtml
 * @param root0.onSave
 * @param root0.onChange
 * @param root0.autoSave
 * @param root0.debounceMs
 * @param root0.readonly
 * @param root0.placeholder
 * @param root0.showVariableHelper
 * @param root0.questionarioId
 * @param root0.autoInsertVariable
 * @param root0.templateId
 * @param root0.templateImages
 * @param root0.showImagesTab
 * @param root0.hideSaveButton
 * @param root0.onSaveStateChange
 */
export function TemplateEditor({
  initialHtml = "",
  onSave,
  onChange,
  autoSave = true,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  readonly = false,
  placeholder = "Digite seu HTML aqui...",
  showVariableHelper = false,
  questionarioId,
  autoInsertVariable = true,
  templateId,
  templateImages = DEFAULT_TEMPLATE_IMAGES,
  showImagesTab,
  hideSaveButton = false,
  onSaveStateChange
}: TemplateEditorProperties) {
  // Determine if images tab should be shown
  const shouldShowImagesTab = showImagesTab ?? !!templateId;

  // State
  const [html, setHtml] = useState(initialHtml);
  const [activeTab, setActiveTab] = useState<string | null>("editor");
  const [validation, setValidation] = useState<ValidationState>({
    isValid: true,
    issues: [],
    hadDangerousContent: false,
    sanitizedHtml: ""
  });
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // Debounced HTML for auto-save
  const [debouncedHtml] = useDebouncedValue(html, debounceMs);

  // Track if this is the first render to avoid auto-save on mount
  const isFirstRender = useRef(true);

  // Capture initial HTML ONCE (prevent re-save loop when template updates)
  const initialHtmlReference = useRef(initialHtml);

  // Ref to CodeMirror for cursor position control
  const editorReference = useRef<ReactCodeMirrorRef>(null);

  // Validate HTML whenever it changes
  useEffect(() => {
    if (!html) {
      setValidation({
        isValid: true,
        issues: [],
        hadDangerousContent: false,
        sanitizedHtml: ""
      });
      return;
    }

    // Sanitize and validate
    const sanitized = sanitizeHTML(html);
    const structureValidation = validateHTMLStructure(sanitized.html);

    setValidation({
      isValid: sanitized.isValid && structureValidation.isValid,
      issues: [...sanitized.issues, ...structureValidation.issues],
      hadDangerousContent: sanitized.hadDangerousContent,
      sanitizedHtml: sanitized.html // Store sanitized HTML for safe preview
    });
  }, [html]);

  // Auto-save when debounced HTML changes
  useEffect(() => {
    // Skip first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Skip if no onSave callback
    if (!onSave) return;

    // Skip if not auto-save
    if (!autoSave) return;

    // Skip if HTML hasn't changed from initial
    if (debouncedHtml === initialHtmlReference.current) return;

    // Skip if invalid HTML
    if (!validation.isValid) return;

    // Trigger save
    const performSave = async () => {
      setIsSaving(true);
      try {
        await onSave(debouncedHtml);
        setLastSaved(new Date());
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Auto-save failed:", error);
      } finally {
        setIsSaving(false);
      }
    };

    void performSave();
  }, [debouncedHtml, onSave, autoSave, validation.isValid]);

  // Handle HTML change
  const handleHtmlChange = useCallback(
    (value: string) => {
      // Normalize HTML when pasting to remove trailing spaces
      const normalized = normalizeHtml(value);
      setHtml(normalized);
      onChange?.(normalized);
    },
    [onChange]
  );

  // Handler for CodeMirror onChange
  const handleEditorChange = useCallback(
    (value: string) => {
      handleHtmlChange(value);
    },
    [handleHtmlChange]
  );

  // Manual save
  const handleManualSave = useCallback(() => {
    if (!onSave) return;
    if (!validation.isValid) return;

    setIsSaving(true);
    void Promise.resolve(onSave(html))
      .then(() => {
        setLastSaved(new Date());
        return null;
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error("Manual save failed:", error);
      })
      .finally(() => {
        setIsSaving(false);
      });
  }, [html, onSave, validation.isValid]);

  // Keep save function in a ref to avoid re-render cycles
  const saveReference = useRef(handleManualSave);
  saveReference.current = handleManualSave;

  // Stable save function that reads from ref
  const stableSave = useCallback(() => {
    saveReference.current();
  }, []);

  // Notify parent of save state changes (only when isSaving or isValid change)
  useEffect(() => {
    onSaveStateChange?.({
      isSaving,
      isValid: validation.isValid,
      save: stableSave
    });
  }, [isSaving, validation.isValid, stableSave, onSaveStateChange]);

  // Handle variable selection from VariableHelper
  const handleVariableSelect = useCallback(
    (variable: string) => {
      if (!autoInsertVariable) return;

      const view = editorReference.current?.view;
      if (!view) {
        // Fallback: append at end if no editor view
        const newHtml = html + variable;
        setHtml(newHtml);
        onChange?.(newHtml);
        return;
      }

      // Get current selection/cursor position
      const { from, to } = view.state.selection.main;

      // Insert variable at cursor position using CodeMirror's transaction
      view.dispatch({
        changes: { from, to, insert: variable },
        selection: { anchor: from + variable.length }
      });

      // Update React state with the new content
      const newHtml = view.state.doc.toString();
      setHtml(newHtml);
      onChange?.(newHtml);

      // Focus the editor
      view.focus();
    },
    [html, onChange, autoInsertVariable]
  );

  // Editor content (reused in both layouts)
  const editorContent = (
    <>
      {/* Header with status */}
      <EditorHeader
        validation={validation}
        isSaving={isSaving}
        lastSaved={lastSaved}
        autoSave={autoSave}
        onSave={onSave}
        handleManualSave={handleManualSave}
        readonly={readonly}
        hideSaveButton={hideSaveButton}
      />

      {/* Validation errors */}
      {validation.issues.length > 0 && (
        <Alert icon={<IconAlertCircle size={16} />} title="Problemas encontrados" color="red" variant="light">
          <Stack gap="xs">
            {validation.issues.map((issue, index) => (
              <Text key={index} size="sm">
                • {issue}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="editor" leftSection={<IconCode size={16} />}>
            Editor
          </Tabs.Tab>
          <Tabs.Tab value="preview-pdf" leftSection={<IconFileTypePdf size={16} />}>
            Preview PDF
          </Tabs.Tab>
          {shouldShowImagesTab && (
            <Tabs.Tab value="images" leftSection={<IconPhoto size={16} />}>
              Imagens {templateImages.length > 0 && `(${templateImages.length})`}
            </Tabs.Tab>
          )}
        </Tabs.List>

        {/* Editor Tab */}
        <Tabs.Panel value="editor" pt="md">
          <CodeMirror
            ref={editorReference}
            value={html}
            onChange={handleEditorChange}
            placeholder={placeholder}
            extensions={CODEMIRROR_EXTENSIONS}
            theme="light"
            height="400px"
            minHeight="300px"
            maxHeight="600px"
            readOnly={readonly}
            basicSetup={CODEMIRROR_BASIC_SETUP}
            style={{
              fontSize: "14px",
              border:
                validation.issues.length > 0
                  ? "1px solid var(--mantine-color-red-6)"
                  : "1px solid var(--mantine-color-gray-4)",
              borderRadius: "var(--mantine-radius-sm)",
              width: "100%",
              maxWidth: "100%",
              overflow: "hidden"
            }}
          />

          {/* Character count */}
          <Group justify="space-between" mt="xs">
            <Text size="xs" c="dimmed">
              {html.length} caracteres
              {html.length > MAX_CHAR_LIMIT && (
                <Text component="span" c="red" ml="xs">
                  (limite: 50KB)
                </Text>
              )}
            </Text>
          </Group>
        </Tabs.Panel>

        {/* Preview PDF Tab */}
        <Tabs.Panel value="preview-pdf" pt="md">
          <TemplatePreview
            htmlTemplate={html}
            questionarioId={questionarioId}
            templateName="Template Preview"
            templateImages={templateImages}
          />
        </Tabs.Panel>

        {/* Images Tab */}
        {shouldShowImagesTab && templateId && (
          <Tabs.Panel value="images" pt="md">
            <ImageManager
              templateId={templateId}
              currentImages={templateImages}
              onVariableCopy={handleVariableSelect}
              maxHeight={400}
            />
          </Tabs.Panel>
        )}
      </Tabs>
    </>
  );

  // Render with or without variable helper
  if (showVariableHelper) {
    return (
      <Grid gutter="md">
        {/* Editor column (70%) */}
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Stack gap="md">{editorContent}</Stack>
        </Grid.Col>

        {/* Variable helper column (30%) */}
        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Paper withBorder p="md" style={{ position: "sticky", top: 0, maxHeight: "100vh", overflowY: "auto" }}>
            <VariableHelper questionarioId={questionarioId} onVariableSelect={handleVariableSelect} compact />
          </Paper>
        </Grid.Col>
      </Grid>
    );
  }

  return <Stack gap="md">{editorContent}</Stack>;
}
